import { useState } from "react";
import { Brain, RotateCcw, CheckCircle2, XCircle, ChevronLeft, ChevronRight, Eye } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, hasFeature } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Generating, ErrorBox, UpgradeCard } from "@/components/course/States";

const Mcq = ({ items, courseId }) => {
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);
  const score = items.filter((q, i) => answers[i] === q.answer_index).length;
  const submit = async () => {
    setDone(true);
    api.post(`/courses/${courseId}/quiz-results`, { score, total: items.length }).catch(() => {});
  };
  return (
    <div className="space-y-4">
      {done && (
        <div className="bg-brand rounded-2xl p-6 text-white" data-testid="quiz-score">
          <p className="eyebrow !text-white/70">Ton score</p>
          <p className="font-display text-4xl font-extrabold">{score} / {items.length}</p>
          <p className="mt-1 text-sm text-white/85">{score === items.length ? "Parfait, tu maîtrises ce cours !" : score >= items.length / 2 ? "Bien joué ! Relis les explications ci-dessous." : "Courage : relis ta fiche puis recommence."}</p>
        </div>
      )}
      {items.map((q, i) => (
        <div key={i} className="card-flat p-5" data-testid={`mcq-question-${i}`}>
          <p className="font-semibold">{i + 1}. {q.question}</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {q.options.map((o, j) => {
              const picked = answers[i] === j, right = j === q.answer_index;
              const cls = done ? (right ? "border-emerald-500/70 bg-emerald-500/10" : picked ? "border-destructive/60 bg-destructive/10" : "opacity-70") : picked ? "border-primary bg-accent" : "hover:border-primary/40";
              return (
                <button key={j} data-testid={`mcq-option-${i}-${j}`} disabled={done} onClick={() => setAnswers((a) => ({ ...a, [i]: j }))}
                  className={`flex items-center gap-2 rounded-xl border px-3.5 py-2.5 text-left text-sm transition-colors ${cls}`}>
                  {done && right && <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />}
                  {done && picked && !right && <XCircle className="h-4 w-4 shrink-0 text-destructive" />}
                  {o}
                </button>
              );
            })}
          </div>
          {done && <p className="mt-3 rounded-xl bg-muted/60 p-3 text-sm text-muted-foreground"><b className="text-foreground">Explication :</b> {q.explanation}</p>}
        </div>
      ))}
      {!done ? (
        <button data-testid="mcq-submit-button" disabled={Object.keys(answers).length < items.length} onClick={submit} className="bg-brand rounded-full px-6 py-3 text-sm font-semibold text-white disabled:opacity-50">Valider mes réponses</button>
      ) : (
        <button data-testid="mcq-retry-button" onClick={() => { setAnswers({}); setDone(false); }} className="rounded-full border px-6 py-3 text-sm font-semibold">Recommencer</button>
      )}
    </div>
  );
};

const Reveal = ({ title, answer, i, kind }) => {
  const [open, setOpen] = useState(false);
  const [mine, setMine] = useState("");
  return (
    <div className="card-flat p-5" data-testid={`${kind}-${i}`}>
      <p className="font-semibold whitespace-pre-wrap">{i + 1}. {title}</p>
      <textarea value={mine} onChange={(e) => setMine(e.target.value)} placeholder="Ta réponse…" data-testid={`${kind}-answer-${i}`}
        className="mt-3 min-h-[80px] w-full rounded-xl border bg-background p-3 text-sm outline-none focus:border-primary/50" />
      <button data-testid={`${kind}-reveal-${i}`} onClick={() => setOpen(!open)} className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-primary"><Eye className="h-4 w-4" /> {open ? "Masquer la correction" : "Voir la correction"}</button>
      {open && <p className="mt-3 whitespace-pre-wrap rounded-xl bg-accent/50 p-3 text-sm">{answer}</p>}
    </div>
  );
};

const Flashcards = ({ cards }) => {
  const [i, setI] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const go = (d) => { setFlipped(false); setI((x) => (x + d + cards.length) % cards.length); };
  return (
    <div className="mx-auto max-w-xl">
      <div className="[perspective:1200px]">
        <button data-testid="flashcard" onClick={() => setFlipped(!flipped)} className={`flip relative block h-64 w-full ${flipped ? "flipped" : ""}`}>
          <div className="flip-face card-flat absolute inset-0 flex items-center justify-center p-8 text-center text-lg font-bold">{cards[i].front}</div>
          <div className="flip-face flip-back bg-brand absolute inset-0 flex items-center justify-center rounded-2xl p-8 text-center text-white">{cards[i].back}</div>
        </button>
      </div>
      <div className="mt-5 flex items-center justify-between">
        <button data-testid="flashcard-prev" onClick={() => go(-1)} className="rounded-full border p-2.5 hover:border-primary/40"><ChevronLeft className="h-4 w-4" /></button>
        <span className="text-sm text-muted-foreground" data-testid="flashcard-counter">{i + 1} / {cards.length} · touche la carte pour la retourner</span>
        <button data-testid="flashcard-next" onClick={() => go(1)} className="rounded-full border p-2.5 hover:border-primary/40"><ChevronRight className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

const SUB = [["mcq", "QCM"], ["open", "Compréhension"], ["cards", "Cartes mémoire"], ["ex", "Exercices"]];

export const QuizTab = ({ c, reload }) => {
  const { user } = useAuth();
  const [sub, setSub] = useState("mcq");
  if (!hasFeature(user, "quiz")) return <UpgradeCard feature="Les quiz intelligents" />;
  const gen = async () => { try { await api.post(`/courses/${c.course_id}/quiz`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  if (c.status !== "ready") return <ErrorBox message="Les notes du cours doivent être prêtes avant de générer un quiz." />;
  if (c.quiz_status === "processing") return <Generating label="Préparation de ton quiz…" sub="QCM, questions, flashcards et exercices corrigés." />;
  if (!c.quiz) return (
    <div className="card-flat flex flex-col items-center px-6 py-16 text-center">
      <Brain className="h-10 w-10 text-violet-500" />
      <p className="mt-4 font-semibold">Vérifie ce que tu as retenu</p>
      <p className="mt-1 text-sm text-muted-foreground">Des questions créées uniquement à partir de ton cours, avec les explications.</p>
      {c.quiz_status === "error" && <p className="mt-3 text-sm text-destructive">{c.quiz_error}</p>}
      <button data-testid="generate-quiz-button" onClick={gen} className="bg-brand mt-6 rounded-full px-6 py-3 text-sm font-semibold text-white">Tester mes connaissances</button>
    </div>
  );
  const q = c.quiz;
  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1 rounded-full bg-muted/70 p-1">
          {SUB.map(([k, l]) => <button key={k} data-testid={`quiz-subtab-${k}`} onClick={() => setSub(k)} className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${sub === k ? "bg-card shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>)}
        </div>
        <button data-testid="regenerate-quiz-button" onClick={gen} className="inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-semibold hover:border-primary/40"><RotateCcw className="h-4 w-4" /> Nouveau quiz</button>
      </div>
      {sub === "mcq" && (q.mcq?.length ? <Mcq items={q.mcq} courseId={c.course_id} /> : <p className="text-sm text-muted-foreground">Aucun QCM.</p>)}
      {sub === "open" && <div className="space-y-4">{q.open_questions?.map((o, i) => <Reveal key={i} i={i} kind="open-question" title={o.question} answer={o.expected_answer} />)}</div>}
      {sub === "cards" && (q.flashcards?.length ? <Flashcards cards={q.flashcards} /> : <p className="text-sm text-muted-foreground">Aucune carte.</p>)}
      {sub === "ex" && <div className="space-y-4">{q.exercises?.map((x, i) => <Reveal key={i} i={i} kind="exercise" title={x.statement} answer={x.solution} />)}</div>}
    </div>
  );
};
